import { ECOUTE_DECK, type EcouteItem, type EcouteLine } from '../data/ecoute-deck.ts';
import { CEFR_ORDER } from '../data/candos.ts';
import { loadSettings, logSession, type Dialect } from '../data/stores.ts';
import { speak, cancelSpeech, speechCapabilities } from '../data/speech.ts';

function pickItemsForLevel(level: string, limit: number): EcouteItem[] {
  const levelIdx = CEFR_ORDER.indexOf(level as never);
  const allowed = new Set(CEFR_ORDER.slice(0, levelIdx + 1));
  const pool = ECOUTE_DECK.filter((i) => allowed.has(i.cefr as never));
  // Rotate by day-of-year so user sees different items across sessions.
  const day = Math.floor(Date.now() / 86_400_000);
  const start = day % Math.max(1, pool.length);
  const cycled: EcouteItem[] = [];
  for (let i = 0; i < pool.length; i++) cycled.push(pool[(start + i) % pool.length]!);
  return cycled.slice(0, limit);
}

export function renderEcoute(): HTMLElement {
  const root = document.createElement('section');
  root.className = 'flex flex-col items-center gap-6 max-w-2xl mx-auto';
  root.innerHTML = '<p class="text-[color:var(--color-ink-muted)] mt-12">Chargement…</p>';
  initSession(root);
  return root;
}

async function initSession(root: HTMLElement): Promise<void> {
  const [settings, caps] = await Promise.all([loadSettings(), speechCapabilities()]);
  const items = pickItemsForLevel(settings.currentLevel, settings.dailyEcouteLimit);

  if (!caps.hasSpeech) {
    root.innerHTML = `
      <div class="p-6 rounded-xl border border-[color:var(--color-rorbu)] bg-[color:var(--color-rorbu)]/10 text-sm">
        Votre navigateur ne prend pas en charge la synthèse vocale.
        Vous pouvez tout de même lire les transcriptions ci-dessous.
      </div>`;
    for (const it of items) root.append(renderItem(it, settings.dialect, true));
    return;
  }

  if (items.length === 0) {
    root.innerHTML = '';
    root.append(renderEmpty());
    return;
  }

  let idx = 0;

  function show(): void {
    root.innerHTML = '';
    root.append(renderHeader(idx, items.length, settings.dialect, caps.voiceCount));
    root.append(renderItem(items[idx]!, settings.dialect, false));
    root.append(renderNav(idx, items.length, () => { cancelSpeech(); idx++; if (idx >= items.length) finish(); else show(); }));
  }

  function finish(): void {
    root.innerHTML = '';
    root.append(renderDone(items.length));
    void logSession({ track: 'ecoute', count: items.length });
  }

  show();
}

function renderHeader(idx: number, total: number, dialect: Dialect, voiceCount: number): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'w-full';
  const pct = Math.round((idx / total) * 100);
  const dialectLabel = dialect === 'siddis'
    ? 'Texte affiché en siddis — lecture audio en bokmål standard (pas d\'audio siddis dans les corpus libres)'
    : 'Bokmål standard';
  wrap.innerHTML = `
    <div class="flex items-center justify-between text-sm text-[color:var(--color-ink-muted)] mb-2">
      <span>Document ${idx + 1} sur ${total}</span>
      <span>${pct}%</span>
    </div>
    <div class="w-full h-1 rounded-full bg-[color:var(--color-border)] mb-2">
      <div class="h-full rounded-full bg-[color:var(--color-fjord-500)] transition-all" style="width:${pct}%"></div>
    </div>
    <p class="text-[10px] text-[color:var(--color-ink-muted)] italic">
      Voix&nbsp;: ${dialectLabel} · ${voiceCount} voix nb-NO détectées
    </p>
  `;
  return wrap;
}

function renderItem(item: EcouteItem, dialect: Dialect, autoReveal: boolean): HTMLElement {
  const card = document.createElement('div');
  card.className = 'w-full rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-6 flex flex-col gap-4';
  card.innerHTML = `
    <div>
      <p class="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded inline-block"
         style="background: var(--tint-butter-bg); color: var(--tint-butter-ink);">
        ${item.cefr} · ${item.title}
      </p>
      <p class="text-sm text-[color:var(--color-ink-muted)] mt-2 italic">${item.scene}</p>
    </div>
  `;

  const playBar = document.createElement('div');
  playBar.className = 'flex items-center gap-3';
  const playAllBtn = document.createElement('button');
  playAllBtn.className = 'px-4 py-2 rounded-lg bg-[color:var(--color-fjord-600)] text-white text-sm hover:bg-[color:var(--color-fjord-700)] transition-colors flex items-center gap-2';
  playAllBtn.innerHTML = '▶ Tout lire';
  playAllBtn.addEventListener('click', () => playAll(item.lines, dialect, lineEls));
  playBar.append(playAllBtn);

  const replayBtn = document.createElement('button');
  replayBtn.className = 'px-3 py-2 rounded-lg border border-[color:var(--color-border)] text-xs hover:bg-[color:var(--color-fjord-50)] transition-colors';
  replayBtn.textContent = '🔁 Plus lentement';
  replayBtn.addEventListener('click', () => playAll(item.lines, dialect, lineEls, 0.7));
  playBar.append(replayBtn);
  card.append(playBar);

  // Transcript (hidden until reveal)
  const transcript = document.createElement('div');
  transcript.className = 'flex flex-col gap-2 mt-2';
  const lineEls: HTMLElement[] = [];
  for (const line of item.lines) {
    const lineEl = document.createElement('div');
    lineEl.className = 'p-3 rounded-lg bg-[color:var(--color-surface)] transition-colors';
    const text = dialect === 'siddis' && line.siddis ? line.siddis : line.bokmal;
    const altLabel = dialect === 'siddis' && line.siddis ? '<span class="text-[10px] uppercase tracking-wider opacity-60">(siddis)</span> ' : '';
    lineEl.innerHTML = `
      ${line.speaker ? `<span class="text-[10px] uppercase tracking-wider text-[color:var(--color-fjord-700)]">${line.speaker}</span>` : ''}
      <p class="text-base mt-1">${altLabel}${text}</p>
      <p class="text-xs text-[color:var(--color-ink-muted)] italic mt-1 fr-translation hidden">${line.french}</p>
    `;
    transcript.append(lineEl);
    lineEls.push(lineEl);
  }
  transcript.classList.toggle('hidden', !autoReveal);
  card.append(transcript);

  const revealBtn = document.createElement('button');
  revealBtn.className = 'self-start text-xs text-[color:var(--color-fjord-600)] hover:underline';
  revealBtn.textContent = '📜 Afficher la transcription';
  revealBtn.addEventListener('click', () => {
    transcript.classList.remove('hidden');
    revealBtn.remove();
    const trBtn = document.createElement('button');
    trBtn.className = 'self-start text-xs text-[color:var(--color-fjord-600)] hover:underline mt-2';
    trBtn.textContent = '🇫🇷 Afficher la traduction';
    trBtn.addEventListener('click', () => {
      for (const tr of transcript.querySelectorAll('.fr-translation')) tr.classList.remove('hidden');
      trBtn.remove();
    });
    card.append(trBtn);
  });
  if (!autoReveal) card.append(revealBtn);

  if (item.questions && item.questions.length > 0) {
    const qWrap = document.createElement('details');
    qWrap.className = 'mt-2 p-3 rounded-lg border border-[color:var(--color-border)]';
    qWrap.innerHTML = `<summary class="text-sm font-medium cursor-pointer">Questions de compréhension (${item.questions.length})</summary>`;
    const qList = document.createElement('ul');
    qList.className = 'mt-2 space-y-2 text-sm';
    for (const q of item.questions) {
      const li = document.createElement('li');
      li.innerHTML = `<p>${q.q}</p><p class="text-xs text-[color:var(--color-ink-muted)] italic mt-1">→ ${q.a}</p>`;
      qList.append(li);
    }
    qWrap.append(qList);
    card.append(qWrap);
  }

  return card;
}

async function playAll(lines: EcouteLine[], _dialect: Dialect, lineEls: HTMLElement[], rate?: number): Promise<void> {
  cancelSpeech();
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const el = lineEls[i];
    el?.classList.add('ring-2', 'ring-[color:var(--color-fjord-400)]');
    // In siddis mode the text on screen is siddis, but the TTS only knows bokmål —
    // we play the bokmål equivalent so the audio is correct, and the UI labels
    // make the discrepancy explicit (the line shows "(siddis)" badge above).
    const text = line.bokmal;
    const opts: { voiceIndex?: number; rate?: number } = { voiceIndex: i };
    if (rate !== undefined) opts.rate = rate;
    await speak(text, opts);
    el?.classList.remove('ring-2', 'ring-[color:var(--color-fjord-400)]');
  }
}

function renderNav(idx: number, total: number, onNext: () => void): HTMLElement {
  const row = document.createElement('div');
  row.className = 'w-full flex justify-end';
  const btn = document.createElement('button');
  btn.className = 'px-5 py-2 rounded-lg bg-[color:var(--color-fjord-600)] text-white text-sm font-medium hover:bg-[color:var(--color-fjord-700)] transition-colors';
  btn.textContent = idx + 1 < total ? 'Document suivant →' : 'Terminer la session';
  btn.addEventListener('click', onNext);
  row.append(btn);
  return row;
}

function renderDone(count: number): HTMLElement {
  const el = document.createElement('div');
  el.className = 'flex flex-col items-center gap-4 mt-12';
  el.innerHTML = `
    <div class="w-16 h-16 rounded-full bg-[color:var(--color-sage)] text-white flex items-center justify-center text-2xl">✓</div>
    <h2 class="text-2xl font-semibold">Session d’écoute terminée</h2>
    <p class="text-[color:var(--color-ink-muted)]">${count} document${count > 1 ? 's' : ''} écouté${count > 1 ? 's' : ''}</p>
    <a href="#/accueil" class="text-sm text-[color:var(--color-fjord-600)] hover:underline">← Retour à l'accueil</a>
  `;
  return el;
}

function renderEmpty(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'flex flex-col items-center gap-4 mt-12';
  el.innerHTML = `
    <p class="text-[color:var(--color-ink-muted)]">Aucun document d’écoute disponible pour votre niveau actuel.</p>
    <a href="#/parametres" class="text-sm text-[color:var(--color-fjord-600)] hover:underline">Ajuster mon niveau →</a>
  `;
  return el;
}
